import React, { useState, useEffect } from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './App.css';
import API_BASE_URL from './config';

// Components
import Navbar from './Components/Navbar/Navbar';
import Shop from './Pages/Shop';
import ShopCategory from './Pages/ShopCategory';
import Product from './Pages/Product';
import Cart from './Pages/Cart';
import LoginSignup from './Pages/LoginSignup';
import Footer from './Components/Footer/Footer';
import ShopContextProvider from './Context/ShopContext';
import Checkout from './Components/Checkout';
import OrderSuccess from './Components/OrderSuccess';
import HorizontalScrollbar from './Components/HorizontalScrollbar/HorizontalScrollbar';
import ScrollToTop from './Components/ScrollToTop';
import PrivacyPolicy from './Components/PrivacyPolicy/PrivacyPolicy';
import TermsOfService from './Components/TermsOfService/TermsOfService';
import CookiesPolicy from './Components/CookiesPolicy/CookiesPolicy';
import ContactUs from './Components/ContactUs/ContactUs';
import OurTeam from './Components/OurTeam/OurTeam';

// New Components
import ProfileDashboard from './Components/ProfileDashboard/ProfileDashboard';
import ProtectedRoute from './Components/ProtectedRoute/ProtectedRoute';
import WhatsAppWidget from './Components/WhatsAppIcon/WhatsAppIcon';
import AboutNepliShop from './Components/AboutNepliShop/AboutNepliShop';

function App() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backendError, setBackendError] = useState(false);

  // Fetch categories from backend
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      console.log('Fetching categories from backend...');
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`${API_BASE_URL}/api/categories`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Categories data received:', data);
      
      if (data.success) {
        setCategories(data.categories || []);
        setBackendError(false);
      } else {
        setCategories([]);
        setBackendError(true);
      }
    } catch (error) {
      console.error('Error fetching categories:', error.message);
      console.error('Full error:', error);
      
      // Set default categories for development
      const defaultCategories = [
        { _id: '1', name: 'Men', slug: 'mens', status: 'active' },
        { _id: '2', name: 'Women', slug: 'womens', status: 'active' },
        { _id: '3', name: 'Kids', slug: 'kids', status: 'active' },
        { _id: '4', name: 'Shoes', slug: 'shoes', status: 'active' }
      ];
      
      setCategories(defaultCategories);
      setBackendError(true);
      
      console.log('Using default categories for development');
    } finally {
      setLoading(false);
    }
  };

  // Generate dynamic routes from categories
  const getCategoryRoutes = () => {
    return categories
      .filter(cat => cat.status === 'active')
      .map(category => (
        <Route 
          key={category._id} 
          path={`/${category.slug}`} 
          element={<ShopCategory category={category.slug} />} 
        />
      ));
  };

  // Layout component for pages with Navbar, HorizontalScrollbar, and Footer
  const DefaultLayout = ({ children }) => (
    <>
      {backendError && (
        <div style={{
          background: '#ffebee',
          color: '#c62828',
          padding: '10px',
          textAlign: 'center',
          fontSize: '14px'
        }}>
          ⚠️ Backend connection issue. Using demo data.
        </div>
      )}
      {!loading && <HorizontalScrollbar categories={categories} />}
      {children}
      <Footer />
    </>
  );

  // Layout for profile pages
  const ProfileLayout = ({ children }) => (
    <>
      {children}
    </>
  );

  // Layout for legal pages
  const LegalPageLayout = ({ children }) => (
    <>
      {children}
      <Footer />
    </>
  );

  // Layout for auth pages
  const AuthPageLayout = ({ children }) => (
    <>
      {children}
      <Footer />
    </>
  );

  return (
    <ShopContextProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Navbar />
        
        <Routes>
          {/* Home route with default layout */}
          <Route path='/' element={
            <DefaultLayout>
              <Shop />
            </DefaultLayout>
          } />
          
          {/* Legacy category routes */}
          <Route path='/mens' element={
            <DefaultLayout>
              <ShopCategory category="men" />
            </DefaultLayout>
          } />
          <Route path='/womens' element={
            <DefaultLayout>
              <ShopCategory category="women" />
            </DefaultLayout>
          } />
          <Route path='/kids' element={
            <DefaultLayout>
              <ShopCategory category="kids" />
            </DefaultLayout>
          } />
          <Route path='/shoes' element={
            <DefaultLayout>
              <ShopCategory category="shoes" />
            </DefaultLayout>
          } />
          
          {/* Dynamic category routes */}
          {getCategoryRoutes()}
          
          {/* Common routes */}
          <Route path='/product/:productId' element={
            <DefaultLayout>
              <Product />
            </DefaultLayout>
          } />
          <Route path='/cart' element={
            <DefaultLayout>
              <Cart />
            </DefaultLayout>
          } />
          <Route path='/checkout' element={
            <DefaultLayout>
              <Checkout />
            </DefaultLayout>
          } />
          <Route path='/order-success' element={
            <DefaultLayout>
              <OrderSuccess />
            </DefaultLayout>
          } />
          
          {/* Profile routes */}
          <Route path='/profile/*' element={
            <ProtectedRoute>
              <ProfileLayout>
                <ProfileDashboard />
              </ProfileLayout>
            </ProtectedRoute>
          } />
          
          <Route path='/orders' element={<Navigate to="/profile?tab=orders" replace />} />
          
          <Route path='/login' element={
            <AuthPageLayout>
              <LoginSignup />
            </AuthPageLayout>
          } />
          
          {/* Legal pages */}
          <Route path='/privacy' element={
            <LegalPageLayout>
              <PrivacyPolicy />
            </LegalPageLayout>
          } />
          <Route path='/terms' element={
            <LegalPageLayout>
              <TermsOfService />
            </LegalPageLayout>
          } />
          <Route path='/contact-us' element={
            <LegalPageLayout>
              <ContactUs />
            </LegalPageLayout>
          } />
          <Route path='/cookies' element={
            <LegalPageLayout>
              <CookiesPolicy />
            </LegalPageLayout>
          } />
          <Route path='/team' element={
            <LegalPageLayout>
              <OurTeam />
            </LegalPageLayout>
          } />
          <Route path='/about-neplishop' element={
            <LegalPageLayout>
              <AboutNepliShop />
            </LegalPageLayout>
          } />
          
          {/* Redirects */}
          <Route path='/men' element={<Navigate to="/mens" replace />} />
          <Route path='/women' element={<Navigate to="/womens" replace />} />
          
          {/* 404 Page */}
          <Route path='*' element={
            <DefaultLayout>
              <div className="page-not-found">
                <h2>404</h2>
                <h3>Page Not Found</h3>
                <p>The page you're looking for doesn't exist or has been moved.</p>
                <a href="/" className="home-link">Return to Home</a>
              </div>
            </DefaultLayout>
          } />
        </Routes>
        
        <WhatsAppWidget />
        
      </BrowserRouter>
      
      <Toaster 
        position="bottom-left"
        reverseOrder={false}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#333',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            fontSize: '14px',
            fontWeight: '500',
            padding: '16px'
          },
          success: {
            style: {
              background: '#10b981',
              color: '#fff'
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#10b981'
            }
          },
          error: {
            style: {
              background: '#ef4444',
              color: '#fff'
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#ef4444'
            }
          }
        }}
      />
    </ShopContextProvider>
  );
}

export default App;

